// Resumo da Competência — esteira de produção fiscal estilo Oneflow (cockpit 2.0, dinâmico).
// Etapas (Escrituração -> Apuração -> Obrigações -> Entrega) + Apuração de impostos + ciclo + entregas.
// Honesto: status derivado de dado real (apurações, lançamentos, reinf).
import { useEffect, useState } from 'react'
import {
  obterClienteApi,
  listarLancamentos, listarApuracoesDAS, listarApuracoesPisCofins,
  listarApuracoesIrpjCsll, listarPendenciasReinf, listarEnviosReinf,
} from '../api'
import { PageHeader, Loading, Empty } from '../components/common'
import { fmtMoeda, competenciaLabel, mesAtual } from '../lib/format'

const COR = { ok: '#34D399', andamento: '#FBBF24', pend: '#FB923C', neutral: '#64748B' }
const LBL = { ok: 'Concluído', andamento: 'Em andamento', pend: 'Pendente', neutral: '—' }

function statusApur(ap) {
  if (!ap) return 'pend'
  const s = (ap.status || '').toLowerCase()
  if (s === 'aprovada' || s === 'aprovado') return 'ok'
  if (s === 'rascunho') return 'andamento'
  return 'andamento'
}
function valorApur(ap) {
  if (!ap) return null
  for (const k of ['valor_total', 'valor', 'total', 'valor_devido', 'imposto', 'das_valor']) {
    if (ap[k] != null) return Number(ap[k])
  }
  return null
}

const STYLE = `
@keyframes rcRise { from{opacity:0;transform:translateY(12px)} to{opacity:1;transform:translateY(0)} }
@keyframes rcPulse { 0%{box-shadow:0 0 0 0 rgba(196,149,106,.45)} 70%{box-shadow:0 0 0 9px rgba(196,149,106,0)} 100%{box-shadow:0 0 0 0 rgba(196,149,106,0)} }
@keyframes rcFlow { to { background-position:24px 0 } }
@keyframes rcGrow { from{width:0} }
.rc-track{ height:6px; border-radius:99px; background:var(--surface-2); overflow:hidden; border:1px solid var(--border); margin:2px 0 16px }
.rc-fill{ height:100%; border-radius:99px; background:linear-gradient(90deg,var(--gold-deep,#8B6550),var(--gold,#C4956A)); box-shadow:0 0 12px rgba(196,149,106,.5); transition:width .6s cubic-bezier(.4,0,.2,1); animation:rcGrow .9s ease both }
.rc-esteira{ display:flex; align-items:stretch; gap:6px; flex-wrap:wrap }
.rc-step{ flex:1 1 150px; cursor:pointer; padding:14px 16px; border-radius:12px; border:1px solid var(--border);
  background:linear-gradient(165deg,var(--surface),var(--surface-2)); transition:transform .2s,box-shadow .2s,border-color .2s;
  animation:rcRise .5s ease both; position:relative; overflow:hidden }
.rc-step::after{ content:""; position:absolute; left:0; right:0; top:0; height:3px; background:var(--c) }
.rc-step:hover{ transform:translateY(-4px); border-color:var(--gold,#C4956A); box-shadow:0 12px 28px rgba(0,0,0,.4) }
.rc-step--cur{ border-color:var(--gold,#C4956A) }
.rc-step--cur::after{ background:repeating-linear-gradient(90deg,var(--c),var(--c) 8px,transparent 8px,transparent 16px); background-size:24px 3px; animation:rcFlow 1s linear infinite }
.rc-num{ width:24px;height:24px;border-radius:99px;display:flex;align-items:center;justify-content:center;font-weight:800;font-size:12px;color:#0a0a0a;flex-shrink:0 }
.rc-step--cur .rc-num{ animation:rcPulse 1.8s infinite }
.rc-conn{ align-self:center;color:var(--text-3,#5A5550);font-size:18px;flex:0 0 auto }
@media(max-width:760px){ .rc-conn{ display:none } }
.rc-imp{ cursor:pointer; padding:15px 16px; border-radius:12px; border:1px solid var(--border); position:relative; overflow:hidden;
  background:linear-gradient(165deg,var(--surface),var(--surface-2)); transition:transform .2s,box-shadow .2s,border-color .2s; animation:rcRise .5s ease both }
.rc-imp::before{ content:""; position:absolute; left:0; top:0; bottom:0; width:3px; background:var(--c) }
.rc-imp:hover{ transform:translateY(-3px); border-color:var(--gold,#C4956A); box-shadow:0 10px 24px rgba(0,0,0,.38) }
.rc-node{ width:26px;height:26px;border-radius:99px;margin:0 auto;display:flex;align-items:center;justify-content:center;font-size:13px;font-weight:800;position:relative;z-index:2;transition:.3s }
.rc-node--cur{ animation:rcPulse 1.8s infinite }
`

export default function ResumoCompetencia({ clienteId, onNavigate }) {
  const [comp, setComp] = useState(mesAtual())
  const [d, setD] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!clienteId) { setLoading(false); return }
    setLoading(true)
    const [ano, mes] = comp.split('-')
    Promise.allSettled([
      obterClienteApi(clienteId),
      // BUG antigo: mandava { competencia } que a API ignora -> contava TODOS
      // os lancamentos em qualquer mes (18 em janeiro, antes da empresa nascer)
      listarLancamentos(clienteId, { ano, mes }),
      listarApuracoesDAS(clienteId, { ano }),
      listarApuracoesPisCofins(clienteId, { ano }),
      listarApuracoesIrpjCsll(clienteId, { ano }),
      listarPendenciasReinf(clienteId, { per_apur: comp }),
      listarEnviosReinf(clienteId, { per_apur: comp }),
    ]).then(([cl, lc, das, pc, ic, pr, er]) => {
      const pick = (r) => (r.status === 'fulfilled' ? (r.value?.items || []) : [])
      const noMes = (arr) => arr.find(a => (a.competencia || a.per_apur || '').startsWith(comp))
      setD({
        cliente: cl.status === 'fulfilled' ? cl.value : null,
        lancCount: lc.status === 'fulfilled' ? (lc.value?.count ?? (lc.value?.items || []).length) : 0,
        das: noMes(pick(das)),
        pisCofins: noMes(pick(pc)),
        irpjCsll: noMes(pick(ic)),
        pendReinf: pr.status === 'fulfilled' ? (pr.value?.count || 0) : 0,
        enviosReinf: pick(er),
      })
      setLoading(false)
    })
  }, [clienteId, comp])

  if (!clienteId) return <Empty title="Selecione um cliente" sub="Use o seletor no topo." />
  if (loading || !d) return <Loading />

  const regime = (d.cliente?.regime_efetivo || d.cliente?.regime_override || d.cliente?.regime || '').toLowerCase()
  const isSimples = regime.includes('simples') || regime.includes('mei')
  const guia = isSimples ? d.das : d.pisCofins
  const guiaNome = isSimples ? 'DAS' : 'PIS/COFINS'
  const guiaGo = isSimples ? 'apuracao-das' : 'apuracao-pis-cofins'
  const escr = d.lancCount > 0 ? 'ok' : 'pend'
  const apur = (d.das || d.pisCofins || d.irpjCsll) ? (statusApur(d.das || d.pisCofins || d.irpjCsll) === 'ok' ? 'ok' : 'andamento') : 'pend'
  const obrig = d.pendReinf > 0 ? 'pend' : (d.enviosReinf.length > 0 ? 'ok' : 'neutral')
  const entrega = 'neutral'

  const etapas = [
    { n: 1, nome: 'Escrituração', st: escr, info: `${d.lancCount} lançamento(s)`, go: 'lancamentos' },
    { n: 2, nome: 'Apuração', st: apur, info: isSimples ? 'DAS (Simples Nacional)' : 'PIS/COFINS · IRPJ/CSLL', go: guiaGo },
    { n: 3, nome: 'Obrigações', st: obrig, info: d.pendReinf > 0 ? `${d.pendReinf} pendência(s)` : 'EFD-Reinf', go: 'retencoes' },
    { n: 4, nome: 'Entrega', st: entrega, info: 'envio ao cliente', go: 'relatorios' },
  ]
  const doneCount = etapas.filter(e => e.st === 'ok').length
  const pct = Math.round((doneCount / etapas.length) * 100)
  const curIdx = etapas.findIndex(e => e.st !== 'ok')

  const impostos = isSimples ? [
    { nome: 'DAS — Simples Nacional', ap: d.das, go: 'apuracao-das' },
  ] : [
    { nome: 'PIS / COFINS', ap: d.pisCofins, go: 'apuracao-pis-cofins' },
    { nome: 'IRPJ / CSLL', ap: d.irpjCsll, go: 'apuracao-irpj-csll' },
  ]

  return (
    <>
      <style>{STYLE}</style>
      <PageHeader title="Resumo da Competência" sub="Esteira de produção fiscal — etapas, apuração e entregas" />

      <div style={{ marginBottom: 18 }}>
        <input type="month" value={comp} onChange={e => setComp(e.target.value)}
          style={{ padding: '8px 10px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text)', fontSize: 13, width: 'auto' }} />
      </div>

      {/* ESTEIRA DE ETAPAS */}
      <div className="card mb-2">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div className="card__title" style={{ margin: 0 }}>Etapas — {competenciaLabel(comp)}</div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'var(--font-data, monospace)' }}>{doneCount}/{etapas.length} concluídas · {pct}%</div>
        </div>
        <div className="rc-track"><div className="rc-fill" style={{ width: `${pct}%` }} /></div>
        <div className="rc-esteira">
          {etapas.map((e, i) => (
            <span key={e.n} style={{ display: 'contents' }}>
              {i > 0 && <span className="rc-conn">›</span>}
              <div className={`rc-step${i === curIdx ? ' rc-step--cur' : ''}`} onClick={() => onNavigate(e.go)} style={{ '--c': COR[e.st], animationDelay: `${i * 80}ms` }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span className="rc-num" style={{ background: COR[e.st] }}>{e.st === 'ok' ? '✓' : e.n}</span>
                  <span style={{ fontWeight: 700, fontSize: 14 }}>{e.nome}</span>
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-2)', marginTop: 8 }}>{e.info}</div>
                <div style={{ fontSize: 11, color: COR[e.st], marginTop: 4, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em' }}>{LBL[e.st]}</div>
              </div>
            </span>
          ))}
        </div>
      </div>

      {/* APURAÇÃO DE IMPOSTOS */}
      <div className="card mb-2">
        <div className="card__title">Apuração de Impostos</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12, marginTop: 10 }}>
          {impostos.map((im, i) => {
            const st = statusApur(im.ap); const v = valorApur(im.ap)
            return (
              <div key={im.nome} className="rc-imp" onClick={() => onNavigate(im.go)} style={{ '--c': im.ap ? COR[st] : 'var(--border)', animationDelay: `${i * 80}ms` }}>
                <div style={{ fontSize: 13, fontWeight: 600, paddingLeft: 6 }}>{im.nome}</div>
                <div style={{ fontSize: 22, fontWeight: 800, margin: '8px 0', paddingLeft: 6, fontFamily: 'var(--font-data, monospace)', letterSpacing: '-.02em', color: v != null ? 'var(--text)' : 'var(--text-2)' }}>{v != null ? fmtMoeda(v) : '—'}</div>
                <span style={{ fontSize: 11, fontWeight: 700, color: im.ap ? COR[st] : 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.4, paddingLeft: 6 }}>{im.ap ? LBL[st] : 'Não apurado'}</span>
              </div>
            )
          })}
        </div>
      </div>

      {/* CICLO DE VIDA DA OBRIGAÇÃO (DAS) */}
      <div className="card mb-2">
        <div className="card__title">Ciclo da obrigação — {guiaNome}</div>
        <div style={{ display: 'flex', alignItems: 'flex-start', marginTop: 16 }}>
          {(() => {
            const aprovada = statusApur(guia) === 'ok'
            const ciclo = [
              { nome: 'Apuração', ok: !!guia, sub: guia ? (guia.status || 'apurada') : 'aguardando' },
              { nome: 'Geração da guia', ok: aprovada, sub: aprovada ? 'guia gerada' : 'após aprovar' },
              { nome: 'Envio ao cliente', ok: false, sub: 'pendente' },
              { nome: 'Pagamento', ok: false, sub: 'não registrado' },
            ]
            const curC = ciclo.findIndex(c => !c.ok)
            return ciclo.map((c, i) => (
              <div key={c.nome} style={{ flex: 1, textAlign: 'center', position: 'relative' }}>
                {i > 0 && <div style={{ position: 'absolute', top: 12, left: '-50%', width: '100%', height: 2, background: ciclo[i - 1].ok ? 'linear-gradient(90deg,#34D399,#34D399)' : 'var(--border)', transition: '.4s' }} />}
                <div className={`rc-node${i === curC ? ' rc-node--cur' : ''}`} style={{
                  background: c.ok ? '#34D399' : 'var(--surface)',
                  border: `2px solid ${c.ok ? '#34D399' : (i === curC ? 'var(--gold,#C4956A)' : 'var(--border)')}`,
                  color: c.ok ? '#0a0a0a' : (i === curC ? 'var(--gold,#C4956A)' : 'var(--text-muted)'),
                }}>{c.ok ? '✓' : (i === curC ? '•' : '')}</div>
                <div style={{ fontSize: 13, fontWeight: 600, marginTop: 8 }}>{c.nome}</div>
                <div style={{ fontSize: 11, color: 'var(--text-2)', marginTop: 2 }}>{c.sub}</div>
              </div>
            ))
          })()}
        </div>
      </div>

      {/* OBRIGAÇÕES & ENTREGAS */}
      <div className="card">
        <div className="card__title">Obrigações & Entregas</div>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, marginTop: 6 }}>
          <tbody>
            {[
              { nome: `${guiaNome} — Guia`, st: statusApur(guia), obs: guia ? (guia.status || '') : 'aguardando apuração' },
              { nome: 'EFD-Reinf (R-4020/R-4010)', st: d.pendReinf > 0 ? 'pend' : (d.enviosReinf.length ? 'ok' : 'neutral'), obs: d.pendReinf > 0 ? `${d.pendReinf} pendência(s)` : `${d.enviosReinf.length} envio(s)` },
              { nome: 'DEFIS / DCTFWeb', st: 'neutral', obs: 'conforme regime/competência' },
            ].map((o, i) => (
              <tr key={i} style={{ borderTop: i ? '1px solid var(--border)' : 'none' }}>
                <td style={{ padding: '11px 4px', fontWeight: 600 }}>{o.nome}</td>
                <td style={{ padding: '11px 4px', color: 'var(--text-2)' }}>{o.obs}</td>
                <td style={{ padding: '11px 4px', textAlign: 'right' }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: COR[o.st], textTransform: 'uppercase', letterSpacing: '.04em' }}>{LBL[o.st]}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  )
}
